import type { CalendarEventStatusDefinition } from '../types/Calendar';

export const SYSTEM_CALENDAR_EVENT_STATUS_CODES = [
  'draft',
  'tentative',
  'confirmed',
  'inWaitingRoom',
  'inProgress',
  'completed',
  'cancelled',
  'noShow',
] as const;

export const FALLBACK_CALENDAR_EVENT_STATUSES: CalendarEventStatusDefinition[] = [
  {
    id: 'system-draft',
    code: 'draft',
    name: 'Draft',
    color: '#94a3b8',
    sortOrder: 0,
    status: 'active',
    isSystem: true,
    isDefault: false,
    countsAsBooking: false,
    suppressesReminders: true,
    createdAt: '',
    updatedAt: '',
  },
  {
    id: 'system-tentative',
    code: 'tentative',
    name: 'Tentative',
    color: '#f59e0b',
    sortOrder: 1,
    status: 'active',
    isSystem: true,
    isDefault: false,
    countsAsBooking: true,
    suppressesReminders: false,
    createdAt: '',
    updatedAt: '',
  },
  {
    id: 'system-confirmed',
    code: 'confirmed',
    name: 'Confirmed',
    color: '#16a34a',
    sortOrder: 2,
    status: 'active',
    isSystem: true,
    isDefault: true,
    countsAsBooking: true,
    suppressesReminders: false,
    createdAt: '',
    updatedAt: '',
  },
  {
    id: 'system-inWaitingRoom',
    code: 'inWaitingRoom',
    name: 'In waiting room',
    color: '#0ea5e9',
    sortOrder: 3,
    status: 'active',
    isSystem: true,
    isDefault: false,
    countsAsBooking: false,
    suppressesReminders: false,
    createdAt: '',
    updatedAt: '',
  },
  {
    id: 'system-inProgress',
    code: 'inProgress',
    name: 'In progress',
    color: '#2563eb',
    sortOrder: 4,
    status: 'active',
    isSystem: true,
    isDefault: false,
    countsAsBooking: false,
    suppressesReminders: false,
    createdAt: '',
    updatedAt: '',
  },
  {
    id: 'system-completed',
    code: 'completed',
    name: 'Completed',
    color: '#0284c7',
    sortOrder: 5,
    status: 'active',
    isSystem: true,
    isDefault: false,
    countsAsBooking: false,
    suppressesReminders: true,
    createdAt: '',
    updatedAt: '',
  },
  {
    id: 'system-cancelled',
    code: 'cancelled',
    name: 'Cancelled',
    color: '#dc2626',
    sortOrder: 6,
    status: 'active',
    isSystem: true,
    isDefault: false,
    countsAsBooking: false,
    suppressesReminders: true,
    createdAt: '',
    updatedAt: '',
  },
  {
    id: 'system-noShow',
    code: 'noShow',
    name: 'No show',
    color: '#b91c1c',
    sortOrder: 7,
    status: 'active',
    isSystem: true,
    isDefault: false,
    countsAsBooking: false,
    suppressesReminders: true,
    createdAt: '',
    updatedAt: '',
  },
];

const SYSTEM_I18N_KEYS: Record<string, string> = {
  draft: 'statusDraft',
  tentative: 'statusTentative',
  confirmed: 'statusConfirmed',
  inWaitingRoom: 'statusInWaitingRoom',
  inProgress: 'statusInProgress',
  completed: 'statusCompleted',
  cancelled: 'statusCancelled',
  noShow: 'statusNoShow',
};

export const normalizeCalendarEventStatusCode = (status?: string | null): string => {
  const trimmed = (status || '').trim();
  if (!trimmed) {
    return 'tentative';
  }

  const lower = trimmed.toLowerCase();
  const systemMatch = SYSTEM_CALENDAR_EVENT_STATUS_CODES.find((code) => code.toLowerCase() === lower);
  if (systemMatch) {
    return systemMatch;
  }

  if (trimmed[0] !== trimmed[0].toLowerCase()) {
    return trimmed[0].toLowerCase() + trimmed.slice(1);
  }

  return trimmed;
};

export const getDefaultCalendarEventStatusCode = (
  statuses: CalendarEventStatusDefinition[],
): string => {
  const active = statuses.filter((item) => item.status === 'active');
  const preferred = active.find((item) => item.isDefault) || active.find((item) => item.code === 'confirmed');
  return preferred?.code || active[0]?.code || 'confirmed';
};

export const getCalendarEventStatusDefinition = (
  statuses: CalendarEventStatusDefinition[],
  status?: string | null,
): CalendarEventStatusDefinition | undefined => {
  const code = normalizeCalendarEventStatusCode(status);
  return statuses.find((item) => item.code.toLowerCase() === code.toLowerCase());
};

export const getCalendarEventStatusLabel = (
  status: string | undefined | null,
  translate: (key: string) => string,
  statuses?: CalendarEventStatusDefinition[],
): string => {
  const code = normalizeCalendarEventStatusCode(status);
  const definition = statuses
    ? getCalendarEventStatusDefinition(statuses, code)
    : undefined;

  if (definition?.name?.trim()) {
    const i18nKey = SYSTEM_I18N_KEYS[code];
    if (definition.isSystem && i18nKey) {
      return translate(i18nKey);
    }
    return definition.name.trim();
  }

  const i18nKey = SYSTEM_I18N_KEYS[code];
  if (i18nKey) {
    return translate(i18nKey);
  }

  return code;
};
